import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ColorTarget } from '@mode-vegas/vegas';
import { IconButtonComponent, IconComponent } from '@mode/capra';
import { OrgTypes } from '@mode/shared/contract-common';
import { ColorPickerContainerComponent, ColorSwatchSelectorComponent } from '@mode/colors/ui';
import { BehaviorSubject, Observable, first } from 'rxjs';
import { BackgroundSettingsToggleComponent } from '../background-settings-toggle/background-settings-toggle.component';
import { ColorFlyoutData } from './color-flyout-menu.component';
import { ColorFlyoutMenuService } from './color-flyout-menu.service';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyMenuModule,
    ColorSwatchSelectorComponent,
    IconComponent,
    IconButtonComponent,
    BackgroundSettingsToggleComponent,
    ColorPickerContainerComponent,
  ],
  selector: 'mode-color-swatch-flyout',
  templateUrl: './color-swatch-flyout.component.html',
  styleUrls: ['./color-swatch-flyout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorSwatchFlyoutComponent {
  @Input() selectedColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  @Input() allColorPalettes: OrgTypes.ColorPalette[] = [];
  @Input() activeColor = '';
  @Input() customIcon: string | undefined = undefined;
  @Input() showTextSettings = false;
  @Input() enableTextSettings = false;
  @Input() isUseAsBackground = false;
  @Input() updateOnMove = true;
  @Input() vizToken = '';

  @Output() colorUpdated = new EventEmitter<{ colorSelected: string; selectedPalette: OrgTypes.ColorPalette }>();
  @Output() markTargetUpdated = new EventEmitter<ColorTarget>();

  @ViewChild('menuTrigger') menuTrigger: ElementRef | undefined;
  @ViewChild('viewContainer', { read: ViewContainerRef }) viewContainer!: ViewContainerRef;

  currentPalette: OrgTypes.ColorPalette | undefined = undefined;
  selectedCustomColor: boolean | undefined = undefined;

  isMenuOpenSubject$: BehaviorSubject<boolean>;
  isMenuOpen$: Observable<boolean>;

  constructor(
    private flyoutMenuService: ColorFlyoutMenuService,
    private analyticsService: AnalyticsService
  ) {
    this.isMenuOpenSubject$ = new BehaviorSubject<boolean>(false);
    this.isMenuOpen$ = this.isMenuOpenSubject$.asObservable();
  }

  onColorSelected(color: string, selectedPalette: OrgTypes.ColorPalette | undefined, isCustom: boolean) {
    this.activeColor = color;
    this.selectedCustomColor = isCustom;

    if (selectedPalette) {
      this.currentPalette = selectedPalette;
      this.colorUpdated.emit({ colorSelected: color, selectedPalette: selectedPalette });
    } else if (this.selectedColorPalette) {
      this.colorUpdated.emit({ colorSelected: color, selectedPalette: this.selectedColorPalette });
    }
  }

  onMarkTargetClick(target: ColorTarget) {
    this.markTargetUpdated.emit(target);
  }

  openMenu() {
    this.isMenuOpenSubject$.next(true);
    const data: ColorFlyoutData = {
      selectedColorPalette: this.currentPalette ?? this.selectedColorPalette,
      allColorPalettes: this.allColorPalettes,
      updateOnMove: this.updateOnMove,
      activeColor: this.activeColor,
      showTextSettings: this.showTextSettings,
      enableTextSettings: this.enableTextSettings,
      isUseAsBackground: this.isUseAsBackground,
      colorSelected: this.onColorSelected.bind(this),
      markTargetSelected: this.onMarkTargetClick.bind(this),
    };
    const position: { top: string | undefined; left: string | undefined } = { top: undefined, left: undefined };
    if (this.menuTrigger?.nativeElement != null) {
      const rect = this.menuTrigger.nativeElement.getBoundingClientRect();
      position.top = rect.top - 4 + 'px';
      position.left = rect.right + 4 + 'px';
    }

    const dialogRef = this.flyoutMenuService.open(data, this.viewContainer, position);
    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        // Check for `selectedCustomColor` value since this runs every time
        // a user closes the flyout, even when the color is not updated
        if (this.selectedCustomColor !== undefined) {
          const updatedText = this.selectedCustomColor ? 'Custom' : 'Palette';
          this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
            location: AnalyticsEvent.SubContext.EditColorsModal,
            action: `Color updated via ${updatedText} tab`,
            context: AnalyticsEvent.Context.Visualization,
            context_id: this.vizToken,
          });

          this.selectedCustomColor = undefined;
        }
        return this.isMenuOpenSubject$.next(false);
      });
  }
}
